// Organization Properties Tab - Lists all properties in the organization
import React from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { Text } from '@/components/common/text';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { OrganizationPropertiesTabSkeleton } from './OrganizationPropertiesTabSkeleton';
import { GlassCard } from '@/components/shared/redesign';
import { SVG_COMPONENTS } from '@/assets/admin';

interface Property {
  id: number;
  marketingName: string;
  externalId?: number;
}

interface OrganizationPropertiesTabProps {
  organizationId: number;
  properties: Property[];
  isLoading: boolean;
}

export const OrganizationPropertiesTab: React.FC<OrganizationPropertiesTabProps> = ({
  organizationId,
  properties,
  isLoading,
}) => {
  const styles = useStyles();
  const { admin, redesign } = useAdminColors();

  if (isLoading) {
    return <OrganizationPropertiesTabSkeleton />;
  }

  if (!properties || properties.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <SVG_COMPONENTS.PROPERTY width={48} height={48} color={redesign.text.dim} />
        <Text style={styles.emptyTitle}>No Properties</Text>
        <Text style={styles.emptyMessage}>
          This organization doesn't have any properties assigned yet.
        </Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Properties</Text>
        <Text style={styles.sectionSubtitle}>
          {properties.length} {properties.length === 1 ? 'property' : 'properties'} in this organization
        </Text>

        <View style={styles.listContainer}>
          {properties.map((property) => (
            <GlassCard key={property.id} padding={16}>
              <View style={styles.propertyItem}>
                <View style={styles.propertyIconContainer}>
                  <SVG_COMPONENTS.PROPERTY width={20} height={20} color={admin.dataCell.textMuted} />
                </View>
                <View style={styles.propertyInfo}>
                  <Text style={styles.propertyName}>{property.marketingName}</Text>
                  {property.externalId && (
                    <Text style={styles.propertyId}>ID: {property.externalId}</Text>
                  )}
                </View>
              </View>
            </GlassCard>
          ))}
        </View>
      </View>
    </ScrollView>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) =>
    StyleSheet.create({
      container: {
        flex: 1,
        paddingHorizontal: 20,
      },
      section: {
        paddingVertical: 20,
      },
      sectionTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: colors.text.primary,
        marginBottom: 4,
      },
      sectionSubtitle: {
        fontSize: 13,
        color: components.admin.dataCell.textMuted,
        marginBottom: 20,
      },
      listContainer: {
        gap: 8,
      },
      propertyItem: {
        flexDirection: 'row',
        alignItems: 'center',
      },
      propertyIconContainer: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: components.admin.formInput.bg,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 12,
      },
      propertyInfo: {
        flex: 1,
      },
      propertyName: {
        fontSize: 14,
        fontWeight: '500',
        color: colors.text.primary,
        marginBottom: 2,
      },
      propertyId: {
        fontSize: 12,
        color: components.admin.dataCell.textMuted,
      },
      loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 40,
      },
      loadingText: {
        marginTop: 12,
        fontSize: 14,
        color: components.admin.dataCell.textMuted,
      },
      emptyContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 40,
      },
      emptyTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: colors.text.primary,
        marginTop: 16,
        marginBottom: 8,
      },
      emptyMessage: {
        fontSize: 14,
        color: components.admin.dataCell.textMuted,
        textAlign: 'center',
      },
    }),
  );
